import { useFormattedTimePreferences } from "@/hooks/useFormattedTimePreferences";
import i18n from "@/i18n";
import * as React from "react";
import { useNavigate } from "react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { showErrorToast, SpaceKpisPage, type SpaceKpisPageTypes } from "turboui";

import Api from "@/api";
import * as Comments from "@/models/comments";
import * as People from "@/models/people";
import * as Kpis from "@/models/kpis";
import { invalidateKpiQueries } from "@/models/kpis/kpiLifecycle";
import { useSubscription } from "@/models/subscriptions";

import { useRichTextHandlers } from "@/hooks/useRichTextHandlers";
import { usePaths } from "@/routes/paths";
import { useLoadedData, useRefresh } from "./loader";
import { KpiEntryComments } from "./KpiEntryComments";

export function Page() {
  const formattedTimePreferences = useFormattedTimePreferences();
  const paths = usePaths();
  const navigate = useNavigate();
  const refresh = useRefresh();
  const queryClient = useQueryClient();
  const { space, kpis, kpi } = useLoadedData();

  const peopleSearch = People.usePeopleSearch({ type: "space", id: space.id! });
  const richTextHandlers = useRichTextHandlers({
    scope: { type: "space", id: space.id! },
    taskList: kpi
      ? { resourceType: "kpi", resourceId: kpi.id, field: "description", canEdit: space.permissions?.canEdit ?? false }
      : null,
  });

  const createKpi = Kpis.useCreateKpi();
  const editKpi = Kpis.useEditKpi();
  const deleteKpi = Kpis.useDeleteKpi();
  const logKpiEntry = Kpis.useLogKpiEntry();
  const editKpiEntry = Kpis.useEditKpiEntry();
  const deleteKpiEntry = Kpis.useDeleteKpiEntry();
  const addKpiAnnotation = Kpis.useAddKpiAnnotation();
  const editKpiAnnotation = Kpis.useEditKpiAnnotation();
  const deleteKpiAnnotation = Kpis.useDeleteKpiAnnotation();
  const createComment = useMutation({
    ...Api.comments.createMutationOptions(),
    onSuccess: () => {
      void invalidateKpiQueries(queryClient);
    },
  });

  const kpisLink = paths.spaceKpisPath(space.id!);
  const parsedKpis = React.useMemo(() => kpis.map((k) => Kpis.parseKpiForTurboUi(paths, k)), [kpis, paths]);
  const selectedKpi = React.useMemo(() => (kpi ? Kpis.parseKpiForTurboUi(paths, kpi) : null), [kpi, paths]);
  const subscriptions = useSubscription({
    subscriptionList: kpi?.subscriptionList,
    entityId: kpi?.id ?? "",
    entityType: "kpi",
    onRefresh: refresh,
  });

  const championSearch = React.useCallback(
    async (query: string) => People.parsePeopleForTurboUi(paths, await peopleSearch(query)),
    [paths, peopleSearch],
  );

  const onCreateKpi = async (input: SpaceKpisPageTypes.NewKpiInput) =>
    run(async () => {
      const res = await createKpi.mutateAsync({
        spaceId: space.id!,
        name: input.name,
        unit: input.unit,
        cadence: input.cadence,
        championId: input.championId,
      });
      await refresh();
      return res.kpi.id;
    });

  const onEditKpi = async (input: SpaceKpisPageTypes.EditKpiInput) =>
    run(async () => {
      await editKpi.mutateAsync({
        kpiId: input.id,
        name: input.name,
        unit: input.unit,
        cadence: input.cadence,
        championId: input.championId,
      });
      await refresh();
      return input.id;
    });

  const onDescriptionChange = async (kpiId: string, description: Record<string, unknown>) => {
    const result = await run(async () => {
      await editKpi.mutateAsync({ kpiId, description: JSON.stringify(description) });
      await refresh();
      return kpiId;
    });

    if (!result.success) {
      showErrorToast(i18n.t("Error"), i18n.t("Failed to update KPI description."));
    }

    return result.success;
  };

  // Deleting the KPI whose page we are on leaves nothing to show, so we return
  // to the list instead of refreshing into a missing KPI.
  const onDeleteKpi = async (kpiId: string) =>
    run(async () => {
      await deleteKpi.mutateAsync({ kpiId });
      if (kpiId === selectedKpi?.id) navigate(kpisLink);
      else await refresh();
    });

  // The value is recorded first and the note is a comment on it, so a failed
  // note must not report the update as failed — that would invite logging the
  // same value twice.
  const onRecordEntry = async (input: SpaceKpisPageTypes.RecordEntryInput) =>
    run(async () => {
      const res = await logKpiEntry.mutateAsync({ kpiId: input.kpiId, value: input.value, period: input.period });

      if (input.comment) {
        try {
          await createComment.mutateAsync({
            entityId: res.entry.id,
            entityType: "kpi_entry",
            content: Comments.stringifyCommentContent(input.comment),
          });
        } catch {
          showErrorToast(i18n.t("Note not posted"), i18n.t("The value was recorded, but the note wasn't saved."));
        }
      }

      await refresh();
    });

  const onEditEntry = async (input: SpaceKpisPageTypes.EditEntryInput) =>
    run(async () => {
      await editKpiEntry.mutateAsync({
        entryId: input.entryId,
        value: input.value,
        period: input.period,
      });
      await refresh();
      return input.entryId;
    });

  const onDeleteEntry = async (entryId: string) =>
    run(async () => {
      await deleteKpiEntry.mutateAsync({ entryId });
      await refresh();
      return entryId;
    });

  const onAddAnnotation = async (input: SpaceKpisPageTypes.AnnotationInput) =>
    run(async () => {
      await addKpiAnnotation.mutateAsync({
        kpiId: input.kpiId,
        date: input.date,
        title: input.title,
      });
      await refresh();
    });

  const onEditAnnotation = async (input: SpaceKpisPageTypes.EditAnnotationInput) =>
    run(async () => {
      await editKpiAnnotation.mutateAsync({
        annotationId: input.id,
        date: input.date,
        title: input.title,
      });
      await refresh();
    });

  const onDeleteAnnotation = async (annotationId: string) =>
    run(async () => {
      await deleteKpiAnnotation.mutateAsync({ annotationId });
      await refresh();
    });

  return (
    <SpaceKpisPage
      formattedTimePreferences={formattedTimePreferences}
      space={{ id: space.id!, name: space.name!, link: paths.spacePath(space.id!) }}
      navigation={[{ to: paths.spacePath(space.id!), label: space.name! }]}
      kpisLink={kpisLink}
      kpis={parsedKpis}
      selectedKpi={selectedKpi}
      currentUser={null}
      canManage={space.permissions?.canEdit ?? false}
      subscriptions={subscriptions}
      championSearch={championSearch}
      richTextHandlers={richTextHandlers}
      onCreateKpi={onCreateKpi}
      onEditKpi={onEditKpi}
      onDescriptionChange={onDescriptionChange}
      onDeleteKpi={onDeleteKpi}
      onRecordEntry={onRecordEntry}
      onEditEntry={onEditEntry}
      onDeleteEntry={onDeleteEntry}
      onAddAnnotation={onAddAnnotation}
      onEditAnnotation={onEditAnnotation}
      onDeleteAnnotation={onDeleteAnnotation}
      canComment={space.permissions?.canComment ?? false}
      renderEntryComments={
        selectedKpi
          ? (entry) => (
              <KpiEntryComments
                entryId={entry.id}
                kpiId={selectedKpi.id}
                spaceId={space.id!}
                canComment={space.permissions?.canComment ?? false}
              />
            )
          : undefined
      }
    />
  );
}

// Adapt a mutation promise to the turboui { success, id?, error? } contract so
// forms can surface inline errors instead of throwing.
async function run(fn: () => Promise<string | void>): Promise<SpaceKpisPageTypes.MutationResult> {
  try {
    const id = await fn();
    return { success: true, id: id ?? undefined };
  } catch (error: any) {
    return { success: false, error: error?.message ?? i18n.t("Something went wrong. Please try again.") };
  }
}
